/**
 * Event System
 * Evaluates timeline events, wandering monster rolls, countdowns, and light sources.
 * Called during the 'events' phase of each stretch.
 */

import type {
  StretchState,
  TimelineEvent,
  Countdown,
  WanderingMonsterResult,
  LightSource,
  EventCondition,
} from './types';
import { FREQUENCY_INTERVALS, LIKELIHOOD_THRESHOLDS } from './types';

// ============================================================================
// MAIN EVALUATION
// ============================================================================

/** Evaluate all events for the current stretch. Mutates state in place. */
export function evaluateEvents(state: StretchState): StretchState {
  const resolution = state.lastResolution!;

  // 1. Evaluate timeline events
  const firedEvents = evaluateTimeline(state);
  resolution.timelineEventsFired = firedEvents;

  // 2. Roll for wandering monsters
  const monsterResult = rollWanderingMonster(state);
  resolution.wanderingMonster = monsterResult;
  if (monsterResult.triggered) {
    state.outcome = 'combat_triggered';
  }

  // 3. Tick countdowns
  const expired = tickCountdowns(state);
  resolution.countdownsExpired = expired;

  // 4. Tick light sources
  const depleted = tickLightSources(state);
  resolution.lightSourcesDepleted = depleted;

  return state;
}

// ============================================================================
// TIMELINE
// ============================================================================

/** Fire timeline events whose triggerStretch <= current stretchNumber. */
export function evaluateTimeline(state: StretchState): TimelineEvent[] {
  const fired: TimelineEvent[] = [];

  for (const event of state.timeline) {
    if (event.fired) continue;
    if (event.triggerStretch > state.stretchNumber) continue;

    // Check optional condition
    if (event.condition && !evaluateCondition(event.condition, state)) continue;

    event.fired = true;
    fired.push(event);

    // Check if this event triggers combat
    if (event.type === 'reinforcement') {
      state.outcome = 'combat_triggered';
    }
  }

  return fired;
}

/** Evaluate an event condition against current state. */
function evaluateCondition(condition: EventCondition, state: StretchState): boolean {
  switch (condition.type) {
    case 'flag_set':
      return condition.flag ? state.triggerFlags.has(condition.flag) : true;
    case 'flag_not_set':
      return condition.flag ? !state.triggerFlags.has(condition.flag) : true;
    case 'player_in_area':
      if (!condition.area || condition.area.length === 0) return true;
      return state.players.some(player =>
        condition.area!.some(cell => cell.x === player.x && cell.y === player.y),
      );
    default:
      return true;
  }
}

// ============================================================================
// WANDERING MONSTERS
// ============================================================================

/** Roll for wandering monster encounter. Uses configurable frequency + likelihood. */
export function rollWanderingMonster(state: StretchState): WanderingMonsterResult {
  const config = state.wanderingMonsters;
  if (config.encounterTable.length === 0) {
    return { checked: false, triggered: false };
  }

  config.stretchesSinceLastCheck += 1;
  const interval = FREQUENCY_INTERVALS[config.frequency];

  if (config.stretchesSinceLastCheck < interval) {
    return { checked: false, triggered: false };
  }

  // Time to check
  config.stretchesSinceLastCheck = 0;
  const threshold = LIKELIHOOD_THRESHOLDS[config.likelihood];

  // Roll d6
  const roll = rollD6();

  // Stealth bonus: if any player is stealthed, reduce effective roll by 1
  const hasStealthBonus = state.players.some(p => p.isStealthed) || state.crawlStrategy.stealthMode;
  const effectiveRoll = hasStealthBonus ? roll + 1 : roll; // Higher = less likely to trigger

  if (effectiveRoll <= threshold) {
    // Encounter triggered — roll on encounter table
    const tableRoll = rollD6();
    const encounter = config.encounterTable.find(
      e => tableRoll >= e.roll[0] && tableRoll <= e.roll[1],
    );

    return {
      checked: true,
      rolled: roll,
      threshold,
      triggered: true,
      encounter: encounter ?? config.encounterTable[0],
    };
  }

  return {
    checked: true,
    rolled: roll,
    threshold,
    triggered: false,
  };
}

/** Simple d6 roll (1-6). */
function rollD6(): number {
  return Math.floor(Math.random() * 6) + 1;
}

// ============================================================================
// COUNTDOWNS
// ============================================================================

/** Tick all countdowns. Returns expired ones. */
export function tickCountdowns(state: StretchState): Countdown[] {
  const expired: Countdown[] = [];

  state.countdowns = state.countdowns.filter(countdown => {
    countdown.remainingStretches -= 1;
    if (countdown.remainingStretches <= 0) {
      expired.push(countdown);

      // Fire the countdown's effect as a timeline event
      const event = countdown.effect;
      event.fired = true;
      state.timeline.push(event);

      // Check if this triggers combat
      if (event.type === 'reinforcement') {
        state.outcome = 'combat_triggered';
      }

      return false; // Remove from active countdowns
    }
    return true;
  });

  return expired;
}

// ============================================================================
// LIGHT SOURCES
// ============================================================================

/** Tick light source durations. Returns depleted sources. */
export function tickLightSources(state: StretchState): LightSource[] {
  const depleted: LightSource[] = [];

  state.crawlStrategy.lightSources = state.crawlStrategy.lightSources.filter(source => {
    source.remainingStretches -= 1;
    if (source.remainingStretches <= 0) {
      depleted.push(source);
      return false; // Remove depleted source
    }
    return true;
  });

  return depleted;
}
